import { createFormContext } from '@mantine/form';
import type { SpendingFormValues } from './by-date.type';

/**
 * Strongly-typed form context for the By Date editor.
 *
 * Why a context (not prop drilling): Mantine's `useForm` returns a NEW `form`
 * object literal on every render (see the note in `useDateSync`). Passing that
 * literal as a prop would invalidate every memoized child's shallow-equality
 * check, even when the form's underlying methods are stable. Routing the form
 * through a single React Context lets us write `useSpendingFormContext()` in
 * leaf components without piping the prop through every intermediate.
 *
 * NOTE on identity: `createFormContext`'s Provider IS `React.Context.Provider`
 * — passing `value={form}` does NOT magically stabilize the literal. Any
 * consumer of `useSpendingFormContext()` still re-renders when ByDate re-renders
 * and a new form literal flows through. The win here is purely ergonomic
 * (typed context surface, no prop drilling), not perf. Per-keystroke render
 * isolation comes from the rollup store + memoized ProjectRow, not from this
 * context.
 *
 * Why uncontrolled mode: per the Mantine docs
 * (https://mantine.dev/form/uncontrolled/), uncontrolled mode is "now the
 * recommended mode for all forms" because `form.values` no longer updates on
 * every keystroke — eliminating the re-render storm that previously caused
 * every project row to re-render whenever any cell changed.
 */
const [SpendingFormProvider, useSpendingFormContext, useSpendingForm] = createFormContext<SpendingFormValues>();

export { SpendingFormProvider, useSpendingForm, useSpendingFormContext };
